import { defineStore } from 'pinia';

export const useCheckoutStore = defineStore('checkout', {
  state: () => ({
    // Core course information
    title: '',
    coursePrefix: '',
    coursePrefixDisplayLabel: '',
    courseStartDate: '',
    courseEndDate: '',
    courseType: '',
    courseInfo: {},
    courseOfferingId: '',
    courseOfferingName: '',
    interestTrack: '',
    sessionName: '',

    // Enrollment details
    enrollmentId: '',
    enrollmentDate: '',
    enrollmentStatus: '',
    courseUrl: '',
    canvasEnrollmentId: null,
    canvasUserId: null,
    canvasCourseId: null,

    // Grading and progress
    currentGrade: '',
    grade: 0,
    finalGrade: '',
    courseCompletedDate: '',
    credits: 0,

    // Pricing and payment
    creditConversionPrice: 0,
    creditEligibilityPrice: 0,
    transactionFee: '',

    // Checkout flow state
    buttonLabel: '',
    buttonAction: '',
    benefitType: '',
    benefitsEligible: false,

    // Navigation context
    from: '',
    lms: '',
    enrollmentType: '', // 'new' for course offering redirect, other values for existing enrollments
    multiEnrollmentType: '', // enrollment type for multi-enrollment support

    // Transaction data
    transactionData: null,

    // Parental consent data (shared with checkout flow)
    parentalConsentData: {
      courseOfferingId: '',
      courseName: '',
      enrollmentId: '',
      from: ''
    }
  }),

  getters: {
    // Computed values for checkout
    pageTitle: (state) => {
      const buttonLabel = state.buttonLabel;
      if (['Upgrade', 'Access Course', 'Enroll and pay'].indexOf(buttonLabel) > -1) {
        return 'Enroll in your course';
      } else if (['Add to Transcript'].indexOf(buttonLabel) > -1) {
        return 'Add to Transcript';
      }
      return '';
    },

    // Check if checkout data is complete
    hasValidCheckoutData: (state) => {
      return !!(state.title && state.coursePrefix && state.enrollmentId);
    },

    // Format course full name
    courseFullName: (state) => {
      return `${state.coursePrefix} ${state.title}`.trim();
    },

    // Check if transaction is benefits eligible
    isBenefitsEligible: (state) => {
      return state.benefitsEligible || state.benefitType === 'benefits';
    },

    // Get all checkout data as an object
    checkoutData: (state) => {
      return {
        title: state.title,
        coursePrefix: state.coursePrefix,
        coursePrefixDisplayLabel: state.coursePrefixDisplayLabel,
        courseStartDate: state.courseStartDate,
        courseEndDate: state.courseEndDate,
        courseType: state.courseType,
        courseInfo: state.courseInfo,
        courseOfferingId: state.courseOfferingId,
        courseOfferingName: state.courseOfferingName,
        interestTrack: state.interestTrack,
        sessionName: state.sessionName,
        enrollmentId: state.enrollmentId,
        enrollmentDate: state.enrollmentDate,
        enrollmentStatus: state.enrollmentStatus,
        courseUrl: state.courseUrl,
        canvasEnrollmentId: state.canvasEnrollmentId,
        canvasUserId: state.canvasUserId,
        canvasCourseId: state.canvasCourseId,
        currentGrade: state.currentGrade,
        grade: state.grade,
        finalGrade: state.finalGrade,
        courseCompletedDate: state.courseCompletedDate,
        credits: state.credits,
        creditConversionPrice: state.creditConversionPrice,
        creditEligibilityPrice: state.creditEligibilityPrice,
        transactionFee: state.transactionFee,
        buttonLabel: state.buttonLabel,
        buttonAction: state.buttonAction,
        benefitType: state.benefitType,
        benefitsEligible: state.benefitsEligible,
        from: state.from,
        lms: state.lms,
        enrollmentType: state.enrollmentType,
        multiEnrollmentType: state.multiEnrollmentType
      };
    }
  },

  actions: {
    // Set complete checkout data (for complex navigation)
    setCheckoutData(payload) {
      // Core course information
      this.title = payload.title || '';
      this.coursePrefix = payload.coursePrefix || '';
      this.coursePrefixDisplayLabel = payload.coursePrefixDisplayLabel || '';
      this.courseStartDate = payload.courseStartDate || '';
      this.courseEndDate = payload.courseEndDate || '';
      this.courseType = payload.courseType || '';
      this.courseInfo = payload.courseInfo || {};
      this.courseOfferingId = payload.courseOfferingId || '';
      this.courseOfferingName = payload.courseOfferingName || '';
      this.interestTrack = payload.interestTrack || '';
      this.sessionName = payload.sessionName || '';

      // Enrollment details
      this.enrollmentId = payload.enrollmentId || '';
      this.enrollmentDate = payload.enrollmentDate || '';
      this.enrollmentStatus = payload.enrollmentStatus || '';
      this.courseUrl = payload.courseUrl || '';
      this.canvasEnrollmentId = payload.canvasEnrollmentId || null;
      this.canvasUserId = payload.canvasUserId || null;
      this.canvasCourseId = payload.canvasCourseId || null;

      // Grading and progress
      this.currentGrade = payload.currentGrade || '';
      this.grade = payload.grade || 0;
      this.finalGrade = payload.finalGrade || '';
      this.courseCompletedDate = payload.courseCompletedDate || '';
      this.credits = payload.credits || 0;

      // Pricing and payment
      this.creditConversionPrice = payload.creditConversionPrice || 0;
      this.creditEligibilityPrice = payload.creditEligibilityPrice || 0;
      this.transactionFee = payload.transactionFee || '';

      // Checkout flow state
      this.buttonLabel = payload.buttonLabel || '';
      this.buttonAction = payload.buttonAction || '';
      this.benefitType = payload.benefitType || '';
      this.benefitsEligible = payload.benefitsEligible || false;

      // Navigation context
      this.from = payload.from || '';
      this.lms = payload.lms || '';
      this.enrollmentType = payload.enrollmentType || '';
      this.multiEnrollmentType = payload.multiEnrollmentType || '';
    },

    // Set transaction data after payment
    setTransactionData(payload) {
      this.transactionData = payload;
    },

    // Clear checkout data (for navigation cleanup)
    clearCheckoutData() {
      this.title = '';
      this.coursePrefix = '';
      this.coursePrefixDisplayLabel = '';
      this.courseStartDate = '';
      this.courseEndDate = '';
      this.courseType = '';
      this.courseInfo = {};
      this.courseOfferingId = '';
      this.courseOfferingName = '';
      this.interestTrack = '';
      this.sessionName = '';
      this.enrollmentId = '';
      this.enrollmentDate = '';
      this.enrollmentStatus = '';
      this.courseUrl = '';
      this.canvasEnrollmentId = null;
      this.canvasUserId = null;
      this.canvasCourseId = null;
      this.currentGrade = '';
      this.grade = 0;
      this.finalGrade = '';
      this.courseCompletedDate = '';
      this.credits = 0;
      this.creditConversionPrice = 0;
      this.creditEligibilityPrice = 0;
      this.transactionFee = '';
      this.buttonLabel = '';
      this.buttonAction = '';
      this.benefitType = '';
      this.benefitsEligible = false;
      this.from = '';
      this.lms = '';
      this.enrollmentType = '';
      this.multiEnrollmentType = '';
      this.transactionData = null;
    },

    // Set parental consent specific data
    setParentalConsentData(payload) {
      this.parentalConsentData = {
        courseOfferingId: payload.courseOfferingId || '',
        courseName: payload.courseName || '',
        enrollmentId: payload.enrollmentId || '',
        from: payload.from || ''
      };
    },

    // Clear parental consent data
    clearParentalConsentData() {
      this.parentalConsentData = {
        courseOfferingId: '',
        courseName: '',
        enrollmentId: '',
        from: ''
      };
    },

    // Update individual fields (for component-specific updates)
    updateTitle(title) {
      this.title = title;
    },

    updateButtonLabel(buttonLabel) {
      this.buttonLabel = buttonLabel;
    },

    updateGrade(grade) {
      this.grade = grade;
    },

    updatePricing({ creditConversionPrice, creditEligibilityPrice }) {
      if (creditConversionPrice !== undefined) {
        this.creditConversionPrice = creditConversionPrice;
      }
      if (creditEligibilityPrice !== undefined) {
        this.creditEligibilityPrice = creditEligibilityPrice;
      }
    }
  }
});
